import { Color, Euler, Material, Vector2, Vector3 } from 'three';
import { MaterialFields } from '../types/material';
import { ColorRepresentation } from '../types/color';
import { EditableFields } from '../types/fields';
import { Position } from '../types/position';
import { gsap } from 'gsap';
import { LightsFields } from '../types/lights';

export function getArray<T>(items: T | T[]): T[] {
    return (Array.isArray(items) ? items : [items]).filter(Boolean);
}

export namespace Composite {

    export function createMaterialParser<T extends Material>(obj: T) {
        const fields = MaterialFields.getAllowedFields(obj);
        return createParser(obj, fields);
    }

    export function createLightParser<T extends THREE.Light>(obj: T) {
        const fields = LightsFields.getAllowedFields(obj);
        return createParser(obj, fields);
    }

    function createParser<T, TKeys extends EditableFields<T>>(obj: T, fields: ReadonlyArray<TKeys>) {
        const helpers = fields.map(field => {
            const v = obj[field] as EditableFields.SupportedTypes;
            if (v == null) {
                return;
            }

            const parser = getValueParser(v);
            const simplifier = getValueSimplifier(v);
            return { field, parser, simplifier: simplifier };
        });

        return {
            /** sets a value from representation to target */
            set: (target: T, state: EditableFields.Representation<T, TKeys>) => {
                helpers.forEach(h => h.parser(state[h.field], target[h.field]));
            },
            /** creates composite simplified representation of a given one, so it can be used for animations */
            get: (state: EditableFields.Representation<T, TKeys>) => {
                const result: EditableFields.SimplifiedRepresentation<T, TKeys> = {};
                helpers.forEach(h => {
                    const s = h.parser(state[h.field]);
                    if (!s) {
                        return;
                    }
                    const v = h.simplifier(s);
                    if (v != null) {
                        result[h.field] = v;
                    }
                });
                return result;
            },
            animate: (tl: gsap.core.Timeline, targets: T | T[], from: EditableFields.Representation<T, TKeys>, to: EditableFields.Representation<T, TKeys>, vars: gsap.TweenVars) => {
                helpers.forEach(helper => {
                    const _from = from && helper.parser(from[helper.field]);
                    const _to = to && helper.parser(to[helper.field]);
                    if (_to == null) {
                        return;
                    }

                    const isObject = typeof _to === 'object';
                    const _targetsArr = getArray(targets);
                    const _targets = isObject
                        ? _targetsArr.map(t => t[helper.field]).filter(Boolean)
                        : _targetsArr;


                    const _fromFinal = isObject ? _from : (_from != null && { [helper.field]: _from });
                    const _toFinal = isObject ? _to : { [helper.field]: _to };

                    if (_fromFinal) {
                        tl.fromTo(
                            _targets,
                            _fromFinal,
                            { ..._toFinal, ...vars },
                            0,
                        );
                        return;
                    }

                    tl.to(
                        _targets,
                        { ..._toFinal, ...vars },
                        0,
                    );
                });
            },
        };
    }

    // Parsers: allow to write to a value using its wide representation
    // OR just convert representation to a value

    type Parser<T> = (state: EditableFields.RepresentationOf<T>, item?: T) => T;
    const NoParser: Parser<any> = (state) => state;

    function getValueParser<T extends EditableFields.SupportedTypes>(item: T) {
        if (item instanceof Color) {
            return ColorRepresentation.parse;
        }

        if (item instanceof Vector2) {
            return Position.parse2d;
        }

        if (item instanceof Vector3) {
            return Position.parse3d;
        }

        if (item instanceof Euler) {
            return Position.parseEuler;
        }

        return NoParser;
    }

    // Simplifiers: allow to convert a value to its simple representation
    // Simple represenations are used in from/to animation keys, so should not be set back to parent object
    function getValueSimplifier(item: EditableFields.SupportedTypes) {
        if (item instanceof Color) {
            return ColorRepresentation.clone;
        }

        if (item instanceof Vector2) {
            return Position.clone2d;
        }

        if (item instanceof Vector3) {
            return Position.clone3d;
        }

        if (item instanceof Euler) {
            return Position.cloneEuler;
        }

        return NoParser;
    }
}

export const getDefaultTimelineVars = (): gsap.TimelineVars => ({
    defaults: {
        immediateRender: false,
    },
});
